/**
 * "Data stores and syncs" (docs section 5): two lanes, each with the writers on the left
 * and the store they write on the right. Lane one: the three sync jobs (trigger, source,
 * what each writes, when the data counts as stale) and the mirror with its three
 * schemas. Lane two: the mail-ingest job and the API, and the app DB with its table
 * groups. On a phone each lane is a stack with the arrow down (the narrow media query).
 */

import { ArrowDown, ArrowRight, Database, Icon, RefreshCw } from "@norican/icons";
import { Card, DescriptionList, Typography } from "@norican/react";
import type { ReactNode } from "react";

import { NARROW_QUERY, useMediaQuery } from "@/hooks/use-media-query";

import { Figure } from "./figure";

export const DATA_STORES_AND_SYNCS_TITLE = "Data stores and syncs";

/** What the drawing shows, for a screen reader. */
export const DATA_STORES_AND_SYNCS_SUMMARY =
  "Two lanes: the sync jobs write the mirror, and the mail-ingest job and the API write the app DB.";

/** A writer: name, trigger and source, what it writes, when its data counts as stale. */
type Writer = readonly [name: string, when: string, writes: string, stale?: string];

/** A store: its name, the database, rows of label and text, and one line under the rows. */
interface Store {
  name: string;
  db: string;
  rows: readonly (readonly [string, string])[];
  foot: string;
}

export const SYNC_JOBS: readonly Writer[] = [
  ["imdp-sync", "Hourly, at minute 17, from IMDP.", "`mirror.*`, then `brain.*`", "6 to 72 hours"],
  // 13 Oct 2026: the two master tables of the deeper search, each behind its own switch
  // row (`oa_customer_master_sync`, `oa_item_master_sync`); they count as stale after 36 hours.
  // 20 Oct 2026: the customer PO of the quoted sales orders (`oa_sales_order_sync`), the same.
  // 21 Oct 2026: the lines of those orders (`oa_sales_order_line_sync`), the same.
  [
    "oa-sync",
    "Hourly, at minute 47, from OmniAnalytics.",
    "`mirror.oa_quote_*`, `mirror.oa_customer_master`, `mirror.oa_item_master`, `mirror.oa_sales_order`, `mirror.oa_sales_order_line`",
    "6 hours (the master tables: 36 hours)",
  ],
  [
    "crm-sync",
    "Nightly, at 03:23 UTC, from Dynamics CE.",
    "`mirror.ce_*`, `brain.crm_*`",
    "30 hours",
  ],
];

const APP_WRITERS: readonly Writer[] = [
  ["mail-ingest", "Every 5 minutes, from the shared mailboxes.", "the case store"],
  ["API", "When a person works on a case.", "work, learning, feedback, training, discussions"],
];

export const MIRROR_DB: Store = {
  name: "Mirror",
  db: "rfq_phase0",
  rows: [
    ["`mirror`", "Copies of the source tables. The switch rows are in `mirror.sync_switch`."],
    ["`brain`", "Tables that the imdp-sync job and the crm-sync job derive from the copies."],
    ["`learn`", "An alias seed. No job syncs it."],
  ],
  foot: "The jobs write as `rfq_syncer`. The API reads as `rfq_reader` and cannot write.",
};

export const APP_DB: Store = {
  name: "App DB",
  db: "rfq_foundry",
  rows: [
    ["Case store", "`rfq_case`, `rfq_case_draft`, `rfq_case_outcome`"],
    ["Work", "`rfq_case_work`, `rfq_case_line_override`"],
    ["Learning", "`rfq_correction`, `rfq_learned_alias`"],
    ["Feedback", "`rfq_feedback`, `rfq_feedback_activity`"],
    ["Caches", "`rfq_mail_translation`, `rfq_vision_reading`"],
    ["Training", "`rfq_training_module`, `rfq_training_learner`"],
    ["Discussions", "`rfq_discussion`, `rfq_discussion_comment`"],
    ["Freshness", "`rfq_sync_state`: each sync job writes one row per table."],
  ],
  foot: "Alembic migrations `0001` to `0028` create the tables. `rfq_case.raw_mime` holds each mail.",
};

const COLUMN = "flex min-w-0 flex-col gap-2";
const LANE = "grid grid-cols-[5fr_auto_6fr] items-center gap-2";

/** Backtick spans in code font, as the documentation page prints them. */
function code(text: string): ReactNode {
  return text.split("`").map((part, index) =>
    index % 2 ? (
      <Typography
        key={index}
        as="code"
        variant="code"
        className="text-[0.9em] break-words normal-case"
      >
        {part}
      </Typography>
    ) : (
      part
    ),
  );
}

function WriterBox({ writer: [name, when, writes, stale] }: { writer: Writer }) {
  return (
    <Card variant="outline" padding="sm" className="flex min-w-0 flex-col gap-0.5">
      <span className="flex min-w-0 items-center gap-1.5">
        <Icon icon={RefreshCw} size="sm" className="text-fg-muted shrink-0" />
        <Typography as="code" variant="code" className="font-semibold break-words">
          {name}
        </Typography>
      </span>
      <Typography variant="body-sm">{when}</Typography>
      <Typography variant="caption" tone="muted" className="break-words">
        Writes: {code(writes)}.{stale && ` Stale after ${stale}.`}
      </Typography>
    </Card>
  );
}

function StoreCard({ store }: { store: Store }) {
  return (
    <Card variant="outline" padding="sm" accent="left" className={COLUMN}>
      <span className="flex min-w-0 flex-wrap items-center gap-1.5">
        <Icon icon={Database} size="sm" className="text-fg-muted shrink-0" />
        <Typography variant="label">{store.name}</Typography>
        <Typography as="code" variant="code" className="break-words">
          {store.db}
        </Typography>
      </span>
      <DescriptionList
        orientation="vertical"
        items={store.rows.map(([label, text]) => ({ label: code(label), value: code(text) }))}
      />
      <Typography variant="caption" tone="muted" className="break-words">
        {code(store.foot)}
      </Typography>
    </Card>
  );
}

/** `layout` overrides the media query for the render tests. Omit it in the app. */
export function DataStoresAndSyncsFigure(props: {
  caption?: string;
  layout?: "lanes" | "stacked";
}) {
  const narrow = useMediaQuery(NARROW_QUERY);
  const form = props.layout ?? (narrow ? "stacked" : "lanes");
  const stacked = form === "stacked";
  const lane = (writers: readonly Writer[], store: Store) => (
    <li className={stacked ? COLUMN : LANE}>
      <div className={COLUMN}>
        {writers.map((writer) => (
          <WriterBox key={writer[0]} writer={writer} />
        ))}
      </div>
      <span className="text-fg-muted flex flex-col items-center text-xs">
        writes
        <Icon icon={stacked ? ArrowDown : ArrowRight} size="sm" label="to" />
      </span>
      <StoreCard store={store} />
    </li>
  );
  return (
    <Figure
      name="data-stores-and-syncs"
      title={DATA_STORES_AND_SYNCS_TITLE}
      caption={props.caption}
    >
      <ol aria-label={DATA_STORES_AND_SYNCS_SUMMARY} data-layout={form} className={COLUMN}>
        {lane(SYNC_JOBS, MIRROR_DB)}
        {lane(APP_WRITERS, APP_DB)}
      </ol>
    </Figure>
  );
}
